import { toast } from 'sonner'

const MAX_FILE_SIZE = 500 * 1024 * 1024 // 500MB
const CHUNK_SIZE = 1 * 1024 * 1024 // 1MB chunks

async function compressImage(file: File): Promise<File> {
  // Create a temporary image element
  const img = document.createElement('img')
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  // Load image using Image element to avoid EXIF auto-rotation
  const imageUrl = URL.createObjectURL(file)
  await new Promise((resolve, reject) => {
    img.onload = resolve
    img.onerror = reject
    img.src = imageUrl
  })

  // Calculate new dimensions while maintaining aspect ratio
  let width = img.width
  let height = img.height
  const maxDimension = 2048 // Max dimension for either width or height

  if (width > height && width > maxDimension) {
    height = (height * maxDimension) / width
    width = maxDimension
  } else if (height > maxDimension) {
    width = (width * maxDimension) / height
    height = maxDimension
  }

  // Set canvas dimensions
  canvas.width = width
  canvas.height = height

  // Draw and compress
  ctx?.drawImage(img, 0, 0, width, height)

  URL.revokeObjectURL(imageUrl)

  // Convert to blob with compression
  const blob = await new Promise<Blob>((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob!),
      'image/jpeg',
      0.7 // Compression quality (0 to 1)
    )
  })

  return new File([blob], file.name, { type: 'image/jpeg' })
}

export const convertBlobToBase64 = async (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

/**
 * Processes the image and creates a preview
 */
export async function processImageWithPreview(
  file: File,
  onStatus?: (status: string) => void
): Promise<{ file: File; url: string; base64: string }> {
  try {
    // Check if it's a HEIC file by extension or type
    const isHeic = file.type === 'image/heic' || /\.(heic|heif)$/i.test(file.name)

    if (isHeic) {
      onStatus?.('Converting HEIC image...')
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetch('/api/image/process', {
        method: 'POST',
        body: formData
      })

      if (!response.ok) {
        throw new Error('Failed to convert HEIC image')
      }

      const jpegBlob = await response.blob()
      file = new File([jpegBlob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
        type: 'image/jpeg'
      })
    }

    onStatus?.('Creating preview...')
    const base64 = await createImagePreview(file)
    const url = URL.createObjectURL(file)

    return {
      file,
      url,
      base64
    }
  } catch (error) {
    console.error('Error processing image:', error)
    throw error
  }
}

/**
 * Creates a square preview for the image
 */
async function cropToSquare(file: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      reject(new Error('Could not get canvas context'))
      return
    }

    img.onload = () => {
      const size = Math.min(img.width, img.height)
      canvas.width = size
      canvas.height = size

      // Calculate cropping coordinates to center the image
      const offsetX = (img.width - size) / 2
      const offsetY = (img.height - size) / 2

      // Draw the cropped image
      ctx.drawImage(img, offsetX, offsetY, size, size, 0, 0, size, size)

      // Convert back to file
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Could not create blob'))
            return
          }
          const croppedFile = new File([blob], file.name, {
            type: 'image/jpeg',
            lastModified: file.lastModified
          })
          resolve(croppedFile)
        },
        'image/jpeg',
        0.9
      )
    }

    img.onerror = () => reject(new Error('Failed to load image'))
    img.src = URL.createObjectURL(file)
  })
}

// Add helper function to generate a square JPEG preview entirely on the client. This avoids
// uploading large (>4.5 MB) images to the /api/image/preview endpoint which exceeds the
// Vercel body limit for serverless functions.

/**
 * Generate a square thumbnail (default 512×512) on the client and return a base64 string.
 * Falls back to the server route if browser APIs are unavailable or the operation fails.
 */
async function generateClientPreview(file: File, size = 512): Promise<string> {
  // Use Image element instead of createImageBitmap to avoid auto-applying EXIF orientation
  const img = new Image();
  const imageUrl = URL.createObjectURL(file);

  await new Promise((resolve, reject) => {
    img.onload = resolve;
    img.onerror = reject;
    img.src = imageUrl;
  });

  const cropSize = Math.min(img.width, img.height);
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Unable to obtain 2D context');
  }

  // Center-crop to square, then scale down to `size`×`size`.
  ctx.drawImage(
    img,
    (img.width - cropSize) / 2,
    (img.height - cropSize) / 2,
    cropSize,
    cropSize,
    0,
    0,
    size,
    size
  );

  URL.revokeObjectURL(imageUrl);

  const blob = await new Promise<Blob>((resolve) => {
    canvas.toBlob((blob) => resolve(blob!), 'image/jpeg', 0.8);
  });

  return await convertBlobToBase64(blob);
}

export async function createImagePreview(file: File): Promise<string> {
  try {
    const isBase64Heic = /^data:image\/heic;base64,/.test(file.name)
    const isHeicFile = file.type === 'image/heic'

    if (isBase64Heic || isHeicFile) {
      let heicFile: File

      if (isBase64Heic) {
        // Convert base64 to File
        const base64Data = file.name.replace(/^data:image\/heic;base64,/, '')
        const binaryString = atob(base64Data)
        const bytes = new Uint8Array(binaryString.length)
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i)
        }
        heicFile = new File([bytes], 'image.heic', { type: 'image/heic' })
      } else {
        heicFile = file
      }

      // Process HEIC file
      const formData = new FormData()
      formData.append('file', heicFile)

      const response = await fetch('/api/image/process', {
        method: 'POST',
        body: formData
      })

      if (!response.ok) {
        throw new Error('Failed to convert HEIC image')
      }

      const jpegBlob = await response.blob()
      file = new File([jpegBlob], 'preview.jpg', { type: 'image/jpeg' })
    }

    // --- NEW: Try to build preview locally first ----------------------------------
    try {
      return await generateClientPreview(file)
    } catch (clientError) {
      console.warn('Client-side preview generation failed, falling back to server', clientError)
    }
    // ------------------------------------------------------------------------------

    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch('/api/image/preview', {
      method: 'POST',
      body: formData
    })

    if (!response.ok) {
      throw new Error('Failed to create preview')
    }

    const blob = await response.blob()
    return await convertBlobToBase64(blob)
  } catch (error) {
    console.error('Error creating preview:', error)
    toast.error('Failed to create image preview')
    throw error
  }
}

/**
 * Process image with size and quality constraints
 */
export async function processImage(file: File): Promise<File> {
  const processedFile = (await processImageWithPreview(file)) as { file: File }
  return processedFile.file
}

async function compressHeicBeforeUpload(file: File): Promise<File> {
  const arrayBuffer = await file.arrayBuffer()
  const blob = new Blob([arrayBuffer], { type: file.type })

  // Use Image element to avoid EXIF auto-rotation
  const img = new Image()
  const imageUrl = URL.createObjectURL(blob)
  await new Promise((resolve, reject) => {
    img.onload = resolve
    img.onerror = reject
    img.src = imageUrl
  })

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  // Scale down if dimensions are too large
  let { width, height } = img
  const maxDimension = 2048

  if (width > maxDimension || height > maxDimension) {
    const ratio = Math.min(maxDimension / width, maxDimension / height)
    width *= ratio
    height *= ratio
  }

  canvas.width = width
  canvas.height = height
  ctx?.drawImage(img, 0, 0, width, height)

  URL.revokeObjectURL(imageUrl)

  const compressedBlob = await new Promise<Blob>((resolve) => {
    canvas.toBlob((blob) => resolve(blob!), 'image/jpeg', 0.7)
  })

  return new File([compressedBlob], file.name, { type: 'image/jpeg' })
}

async function* createImageChunks(file: File) {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE)
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE
    const end = Math.min(start + CHUNK_SIZE, file.size)
    const chunk = file.slice(start, end)
    yield {
      chunk,
      index: i,
      total: totalChunks,
      isLast: i === totalChunks - 1
    }
  }
}

async function streamProcessImage(file: File, onProgress?: (status: string) => void) {
  const chunks = createImageChunks(file)
  const sessionId = Math.random().toString(36).substring(7)

  for await (const { chunk, index, total, isLast } of chunks) {
    const formData = new FormData()
    formData.append('chunk', chunk)
    formData.append('sessionId', sessionId)
    formData.append('index', index.toString())
    formData.append('total', total.toString())
    formData.append('filename', file.name)
    formData.append('isLast', isLast.toString())

    onProgress?.(`Processing ${Math.round(((index + 1) / total) * 100)}%`)

    const response = await fetch('/api/image/process', {
      method: 'POST',
      body: formData
    })

    if (!response.ok) {
      throw new Error(await response.text())
    }

    if (isLast) {
      return await response.blob()
    }
  }
}

async function convertAndShrinkHeic(file: File): Promise<File> {
  // Create a temporary image from the HEIC file
  const heicBlob = new Blob([file], { type: file.type })
  const img = document.createElement('img')
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  // Load HEIC image using browser's built-in decoder
  const imageUrl = URL.createObjectURL(heicBlob)
  await new Promise((resolve, reject) => {
    img.onload = resolve
    img.onerror = reject
    img.src = imageUrl
  })

  // Calculate dimensions while maintaining aspect ratio
  let { width, height } = img
  const maxDimension = 2048

  if (width > height && width > maxDimension) {
    height = (height * maxDimension) / width
    width = maxDimension
  } else if (height > maxDimension) {
    width = (width * maxDimension) / height
    height = maxDimension
  }

  // Set canvas dimensions and draw
  canvas.width = width
  canvas.height = height
  ctx?.drawImage(img, 0, 0, width, height)

  // Convert to JPEG with compression
  const blob = await new Promise<Blob>((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob!),
      'image/jpeg',
      0.8 // Higher quality for HEIC conversion
    )
  })

  URL.revokeObjectURL(imageUrl)
  return new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
    type: 'image/jpeg'
  })
}

async function shrinkHeicBeforeProcessing(file: File): Promise<File> {
  // First convert HEIC to JPEG on server
  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch('/api/image/process', {
    method: 'POST',
    body: formData
  })

  if (!response.ok) {
    throw new Error('Failed to convert HEIC image')
  }

  // Now we have a JPEG we can work with
  const jpegBlob = await response.blob()

  // Use Image element to avoid EXIF auto-rotation
  const img = new Image()
  const imageUrl = URL.createObjectURL(jpegBlob)
  await new Promise((resolve, reject) => {
    img.onload = resolve
    img.onerror = reject
    img.src = imageUrl
  })

  // Calculate scaling for large files
  let { width, height } = img
  const targetSize = 6000000 // 6MB target
  const currentSize = jpegBlob.size
  const scaleFactor = Math.sqrt(targetSize / currentSize)

  width = Math.floor(width * scaleFactor)
  height = Math.floor(height * scaleFactor)

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  ctx?.drawImage(img, 0, 0, width, height)

  URL.revokeObjectURL(imageUrl)

  const blob = await new Promise<Blob>((resolve) => {
    canvas.toBlob((blob) => resolve(blob!), 'image/jpeg', 0.7)
  })

  return new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
    type: 'image/jpeg'
  })
}

// --------------

/**
 * Compress a File for storage upload so that when the server
 * fetches it back for model consumption it's already under 5MB
 * (Bedrock's base64 limit). Targets ~3.5MB raw bytes which is
 * ~4.7MB in base64 — safely under the 5MB wire limit.
 *
 * Outputs JPEG (broad model support, good compression).
 * Iterates dimension + quality until target is met.
 */
const UPLOAD_TARGET_BYTES = 3.5 * 1024 * 1024 // 3.5MB raw

export async function compressFileForUpload(file: File): Promise<File> {
  const img = new Image()
  const imageUrl = URL.createObjectURL(file)

  await new Promise((resolve, reject) => {
    img.onload = resolve
    img.onerror = reject
    img.src = imageUrl
  })

  URL.revokeObjectURL(imageUrl)

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return file

  // Step table: [maxDimension, quality]
  const steps: [number, number][] = [
    [2048, 0.80],
    [1800, 0.70],
    [1600, 0.60],
    [1400, 0.50],
    [1200, 0.42],
    [1000, 0.35]
  ]

  for (const [maxDim, quality] of steps) {
    let width = img.width
    let height = img.height

    if (width > maxDim || height > maxDim) {
      const ratio = Math.min(maxDim / width, maxDim / height)
      width = Math.round(width * ratio)
      height = Math.round(height * ratio)
    }

    canvas.width = width
    canvas.height = height
    ctx.clearRect(0, 0, width, height)
    ctx.drawImage(img, 0, 0, width, height)

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', quality)
    })

    if (blob && blob.size <= UPLOAD_TARGET_BYTES) {
      return new File(
        [blob],
        file.name.replace(/\.[^.]+$/, '.jpg'),
        { type: 'image/jpeg' }
      )
    }
  }

  // Final fallback: 800px at 30%
  const ratio = Math.min(800 / img.width, 800 / img.height, 1)
  canvas.width = Math.round(img.width * ratio)
  canvas.height = Math.round(img.height * ratio)
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

  const finalBlob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', 0.30)
  })

  if (finalBlob) {
    return new File(
      [finalBlob],
      file.name.replace(/\.[^.]+$/, '.jpg'),
      { type: 'image/jpeg' }
    )
  }

  return file
}
